import React from 'react';
import { ExcelData, ProjectFile } from './wizardTypes';

interface WizardStep3Props {
    memberListFile: File | null;
    handleFileChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
    uploadExcel: () => void;
    uploading: boolean;
    excelData: ExcelData | null;
    selectedTableNumberField: string;
    setSelectedTableNumberField: (field: string) => void;
    selectedStatusFields: string[];
    setSelectedStatusFields: (fields: string[]) => void;
    projectFiles: ProjectFile[];
    fileSource: 'upload' | 'existing';
    setFileSource: (source: 'upload' | 'existing') => void;
    selectedProjectFileId: number | null;
    setSelectedProjectFileId: (id: number | null) => void;
    loadExistingFile: () => void;
    loadingExistingFile: boolean;
}

const WizardStep3: React.FC<WizardStep3Props> = ({
    memberListFile,
    handleFileChange,
    uploadExcel,
    uploading,
    excelData,
    selectedTableNumberField,
    setSelectedTableNumberField,
    selectedStatusFields,
    setSelectedStatusFields,
    projectFiles,
    fileSource,
    setFileSource,
    selectedProjectFileId,
    setSelectedProjectFileId,
    loadExistingFile,
    loadingExistingFile,
}) => {
    const handleStatusFieldToggle = (field: string) => {
        if (selectedStatusFields.includes(field)) {
            setSelectedStatusFields(selectedStatusFields.filter(f => f !== field));
        } else {
            setSelectedStatusFields([...selectedStatusFields, field]);
        }
    };

    const getFilenameFromPath = (filepath: string): string => {
        return filepath.split('/').pop() || filepath;
    };

    const getUniqueStatusValues = (): string[] => {
        if (!excelData) return [];
        
        // Find columns that might be status columns (case-insensitive)
        const statusColumn = excelData.columns.find(col => 
            col.toLowerCase().includes('status')
        );
        
        if (!statusColumn) return [];
        
        // Get unique values from the status column
        const uniqueValues = new Set<string>();
        excelData.data.forEach((row) => {
            const value = row[statusColumn];
            if (value !== undefined && value !== null && value !== '') {
                uniqueValues.add(String(value));
            }
        });
        
        return Array.from(uniqueValues).sort();
    };

    return (
        <div className="wizard-step">
            <h3>Mitgliederliste auswählen</h3>
            
            <div className="upload-info">
                <p>Wählen Sie eine Excel-Datei mit der Mitgliederliste. Diese wird zur Ermittlung der Tabler-Anzahl genutzt.</p>
            </div>

            <div className="form-group">
                <label>Quelle</label>
                <div className="radio-group">
                    <label className="radio-label">
                        <input
                            type="radio"
                            value="upload"
                            checked={fileSource === 'upload'}
                            onChange={(e) => setFileSource(e.target.value as 'upload' | 'existing')}
                        />
                        <span>Neue Datei hochladen</span>
                    </label>
                    <label className="radio-label">
                        <input
                            type="radio"
                            value="existing"
                            checked={fileSource === 'existing'}
                            onChange={(e) => setFileSource(e.target.value as 'upload' | 'existing')}
                            disabled={projectFiles.length === 0}
                        />
                        <span>Vorhandene Projektdatei verwenden {projectFiles.length === 0 && '(keine verfügbar)'}</span>
                    </label>
                </div>
            </div>

            {fileSource === 'upload' && (
                <div className="form-group">
                    <label htmlFor="memberList">Excel-Datei</label>
                <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                    <input
                        type="file"
                        id="memberList"
                        accept=".xlsx,.xls"
                        onChange={handleFileChange}
                        style={{ flex: 1 }}
                    />
                    {memberListFile && (
                        <button
                            type="button"
                            className="wizard-btn wizard-btn-submit"
                            onClick={uploadExcel}
                            disabled={uploading}
                            style={{ whiteSpace: 'nowrap' }}
                        >
                            {uploading ? 'Wird hochgeladen...' : 'Hochladen'}
                        </button>
                    )}
                </div>
                {memberListFile && !excelData && (
                    <div className="file-info">
                        Ausgewählt: {memberListFile.name}
                    </div>
                )}
                </div>
            )}

            {fileSource === 'existing' && (
                <div className="form-group">
                    <label htmlFor="existingFile">Projektdatei auswählen</label>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <select
                            id="existingFile"
                            value={selectedProjectFileId || ''}
                            onChange={(e) => setSelectedProjectFileId(Number(e.target.value))}
                            className="wizard-select"
                            style={{ flex: 1 }}
                        >
                            <option value="">-- Bitte wählen --</option>
                            {projectFiles.map((file) => (
                                <option key={file.id} value={file.id}>
                                    {getFilenameFromPath(file.filepath)}
                                </option>
                            ))}
                        </select>
                        {selectedProjectFileId && (
                            <button
                                type="button"
                                className="wizard-btn wizard-btn-submit"
                                onClick={loadExistingFile}
                                disabled={loadingExistingFile}
                                style={{ whiteSpace: 'nowrap' }}
                            >
                                {loadingExistingFile ? 'Lädt...' : 'Laden'}
                            </button>
                        )}
                    </div>
                </div>
            )}

            {excelData && (
                <>
                    <div className="upload-success">
                        <p>✓ Datei erfolgreich hochgeladen: {excelData.rowCount} Zeilen gefunden</p>
                    </div>

                    <div className="form-group">
                        <label htmlFor="tableNumberField">Feld für Tischnummer</label>
                        <select
                            id="tableNumberField"
                            value={selectedTableNumberField}
                            onChange={(e) => setSelectedTableNumberField(e.target.value)}
                            className="wizard-select"
                        >
                            <option value="">-- Bitte wählen --</option>
                            {excelData.columns.map((col) => (
                                <option key={col} value={col}>
                                    {col}
                                </option>
                            ))}
                        </select>
                    </div>

                    <div className="form-group">
                        <label>Status-Werte zur Zählung</label>
                        {getUniqueStatusValues().length > 0 ? (
                            <>
                                <div className="checkbox-group">
                                    {getUniqueStatusValues().map((value) => (
                                        <label key={value} className="checkbox-label">
                                            <input
                                                type="checkbox"
                                                checked={selectedStatusFields.includes(value)}
                                                onChange={() => handleStatusFieldToggle(value)}
                                            />
                                            {value}
                                        </label>
                                    ))}
                                </div>
                                {selectedStatusFields.length > 0 && (
                                    <div className="selection-info">
                                        Ausgewählt: {selectedStatusFields.join(', ')}
                                    </div>
                                )}
                            </>
                        ) : (
                            <div className="selection-info">
                                Keine Status-Spalte gefunden. Bitte stellen Sie sicher, dass die Excel-Datei eine Spalte mit "Status" im Namen enthält.
                            </div>
                        )}
                    </div>
                </>
            )}
        </div>
    );
};

export default WizardStep3;
